import { memo, useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchActiveUser, getActiveUser } from "services/api/users";
import { updateCurrentMfaAPI } from "services/api/users/api";

import { Button } from "ui/components/Button/Button";
import { Badge } from "ui/components/Badge";
import { Loader } from "ui/components/Loader";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";

import { MfaInfoCardProps } from "./types";
import MfaEnableCard from "./components/MfaEnableCard/MfaEnableCard";

/**
 * Dvostruka provjera prijave kodom iz e-pošte. Uključivanje ide kroz potvrdu koda
 * (MfaEnableCard); isključivanje traži samo potvrdu u dijalogu.
 */
const MfaInfoCard: React.FC<MfaInfoCardProps> = () => {
  const dispatch = useDispatch();
  const { createDialog } = useDialog();

  const { user } = useSelector(getActiveUser);
  const [showEnableModal, setShowEnableModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hasEmail = !!user?.email;
  const enabled = user?.twoFactorEnabled === true;

  const handleEnableClick = useCallback(() => {
    setShowEnableModal(true);
  }, []);

  const handleEnableSuccess = useCallback(() => {
    setShowEnableModal(false);
    dispatch(fetchActiveUser() as any);
  }, [dispatch]);

  const handleEnableClose = useCallback(() => {
    setShowEnableModal(false);
  }, []);

  const disable = useCallback(async () => {
    setIsLoading(true);

    try {
      await updateCurrentMfaAPI({ enabled: false });
      dispatch(fetchActiveUser() as any);
    } catch {
      // Poruka je otišla kroz snackbar.
    }

    setIsLoading(false);
  }, [dispatch]);

  const handleDisableClick = useCallback(() => {
    createDialog({
      title: "Isključivanje dvostruke provjere",
      message:
        "Nakon isključivanja za prijavu je dovoljna lozinka. Želite li nastaviti?",
      confirmText: "Isključi",
      onConfirm: disable,
    });
  }, [createDialog, disable]);

  if (!user) return <Loader isLoading={true}> </Loader>;

  return (
    <>
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-32">
        <div className="flex flex-col flex-1 max-w-[300px] ">
          <div>
            <p className="text-base font-bold text-slate-600 py-3">
              Dvostruka provjera prijave
            </p>
            <p className="text-sm">
              Uz lozinku, pri prijavi upisujete i kod koji stiže na vašu email adresu.
            </p>
          </div>
        </div>
        <div className="flex justify-between items-center flex-1 px-3 my-3">
          <div className="flex flex-col min-w-[530px] gap-2">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-700">Status</span>
              <Badge
                text={enabled ? "Uključeno" : "Isključeno"}
                variant={enabled ? "success" : "secondary"}
                showDot
              />
            </div>
            <p className="text-sm text-slate-500">
              {hasEmail
                ? `Kod se šalje na ${user.email}.`
                : "Za uključivanje račun mora imati email adresu. Dodajte je u osobnim podacima."}
            </p>
          </div>
          <div>
            {enabled ? (
              <Button
                variant="outlined"
                children="Isključi"
                onClick={handleDisableClick}
                disabled={isLoading}
              />
            ) : (
              <Button
                variant="outlined"
                children="Uključi"
                onClick={handleEnableClick}
                startIcon="/media/icons/plus.svg"
                disabled={!hasEmail || isLoading}
              />
            )}
          </div>
        </div>
      </div>
      <MfaEnableCard
        show={showEnableModal}
        onSuccess={handleEnableSuccess}
        onClose={handleEnableClose}
      />
      <Loader isLoading={isLoading}> </Loader>
    </>
  );
};

export default memo(MfaInfoCard);
